import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, TextField, Button } from '@mui/material';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import DateFieldCalendar from 'packages/novous_web/src/common/components/DateFieldCalender';

interface DriverCoachingCommentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  driverId: string;
  coachingSessionId: string;
  title?: string;
  desc?: string;
}

const DriverCoachingCommentModal: React.FC<DriverCoachingCommentModalProps> = ({
  open,
  onClose,
  onSuccess,
  driverId,
  coachingSessionId,
  title = 'Add Comment / Training Date',
  desc = 'Add a comment and/or training date for this coaching session.',
}) => {
  const { showToast } = useToast();
  const [comment, setComment] = useState<string>('');
  const [trainingDate, setTrainingDate] = useState<string | null>(null);
  const [commentError, setCommentError] = useState<string>('');
  const [formError, setFormError] = useState<string>('');

  const {
    data,
    error: requestError,
    loading,
    fetchData,
    resetData,
  } = useAxios<any>(API_ENDPOINTS.coaching.DRIVER_COACHING_ADD_TRAINING_DATE_COMMENT, 'POST');

  useEffect(() => {
    if (!open) {
      setComment('');
      setTrainingDate(null);
      setCommentError('');
      setFormError('');
    }
  }, [open]);

  useEffect(() => {
    if (requestError) {
      showToast(requestError || 'Something went wrong!', 'error');
      resetData();
    }
  }, [requestError, resetData, showToast]);

  useEffect(() => {
    if (data?.message) {
      showToast(data.message, 'success');
      resetData();
      setComment('');
      setTrainingDate(null);
      setCommentError('');
      setFormError('');
      onSuccess?.();
      onClose();
    }
  }, [data, resetData, showToast, onClose, onSuccess]);

  const validateComment = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (!/[a-zA-Z0-9]/.test(trimmed)) return 'Comment must contain at least one alphanumeric character.';
    if (trimmed.length < 3) return 'Comment must be at least 3 characters long.';
    if (trimmed.length > 200) return 'Comment cannot exceed 200 characters.';
    return '';
  };

  const handleClose = () => {
    setComment('');
    setTrainingDate(null);
    setCommentError('');
    setFormError('');
    onClose();
  };

  const handleSave = () => {
    const trimmedComment = comment.trim();
    const hasComment = !!trimmedComment;
    const hasTrainingDate = !!trainingDate;

    if (!hasComment && !hasTrainingDate) {
      setFormError('Please add a comment or select a training date.');
      return;
    }

    if (!driverId || !coachingSessionId) {
      showToast('Missing driver or coaching session details.', 'error');
      return;
    }

    if (hasComment) {
      const validationError = validateComment(comment);
      if (validationError) {
        setCommentError(validationError);
        return;
      }
    }

    const payload: {
      driverId: string;
      coachingSessionId: string;
      comment?: string;
      trainingDate?: string;
    } = {
      driverId,
      coachingSessionId,
    };

    if (hasComment) {
      payload.comment = trimmedComment;
    }
    if (hasTrainingDate && trainingDate) {
      payload.trainingDate = trainingDate;
    }

    fetchData(payload);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="add-coaching-comment-training-date-modal"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        sx={{
          width: { xs: '92vw', sm: 520, md: 560 },
          maxHeight: '90vh',
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: '16px',
          border: '1px solid #F0EFF8',
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflow: 'auto',
        }}
      >
        <Typography variant="h6">{title}</Typography>
        <Typography variant="button" fontWeight={400} color="secondary">
          {desc}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography>Comments</Typography>
          <TextField
            label="Write here"
            multiline
            rows={4}
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              setCommentError('');
              setFormError('');
            }}
            variant="outlined"
            fullWidth
            error={!!commentError}
            helperText={commentError}
            InputLabelProps={{ sx: { fontWeight: 400 } }}
            InputProps={{ sx: { fontWeight: 400 } }}
          />
        </Box>

        <DateFieldCalendar
          label="Training Date"
          name="trainingDate"
          value={trainingDate}
          onChange={(date) => {
            setTrainingDate(date);
            setFormError('');
          }}
          format="YYYY-MM-DD"
        />

        {formError && (
          <Typography variant="caption" color="error">
            {formError}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 'auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
            sx={{ fontWeight: 500, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
            sx={{ fontWeight: 500, textTransform: 'none', ml: { xs: 0, sm: 'auto' } }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default DriverCoachingCommentModal;
